import { inject, Injectable } from '@angular/core'
import { FeathersService } from '../../services/feathers.service'
import { ClientService, CustomMethod } from '@feathersjs/client'
import { AssistantAssignment } from '../../models/assistantAssignment'
import { Assistance } from '../../models/assistance'
import { AssistantAvailability } from '../../models/assistantAvailability'
import { SERVICE_PATHS } from '../../services/FeathersServicesCustomMethodsDeclaration'

export type SkippedAssignmentReason = 'has_care_logs'

export interface SkippedAssignment {
  assignment: AssistantAssignment
  reason: SkippedAssignmentReason
}

export interface RemovalResult {
  removedAssignmentIds: number[]
  skippedAssignments: SkippedAssignment[]
}

export interface AssignmentsSetResult {
  assignments: AssistantAssignment[]
  skippedAssignments: SkippedAssignment[]
}

interface UpdateData {
  assistanceURID: string
  availabilitiesToSetURIDs: string[]
  nonAvailabilityAssistantsToSetIDs: number[]
}

@Injectable({
  providedIn: 'root',
})
export class AssignmentsManagerService {
  private feathersService = inject(FeathersService)

  private readonly assignmentsManagerBEService: ClientService & {
    removeAllFromAssistance?: CustomMethod<number, RemovalResult>
    advanceStatus?: CustomMethod<number[], AssistantAssignment>
    advanceAssistanceStatus?: CustomMethod<number, Assistance>
    cancelAssistance?: CustomMethod<number, Assistance>
  } = this.feathersService.service(SERVICE_PATHS.ASSIGNMENTS_MANAGER)

  /**
   * Remove all assignments from the assistance.
   *
   * @param assistanceId The ID of the assistance.
   */
  async removeAllAssignmentsFromAssistance(assistanceId: number): Promise<RemovalResult> {
    return (await this.assignmentsManagerBEService.removeAllFromAssistance(assistanceId))
  }

  async advanceStatus(assignmentIds: number[]): Promise<AssistantAssignment> {
    return await this.assignmentsManagerBEService.advanceStatus(assignmentIds)
  }

  async advanceAssistanceStatus(assistanceId: number): Promise<Assistance> {
    return await this.assignmentsManagerBEService.advanceAssistanceStatus(assistanceId)
  }

  async cancelAssistance(assistanceId: number): Promise<Assistance> {
    return await this.assignmentsManagerBEService.cancelAssistance(assistanceId)
  }

  /**
   * Sets assignments for assistance.
   *
   * @param assistanceURID The URID of the assistance.
   * @param availabilities The list of availabilities to set.
   */
  async setAssignments(assistanceURID: string, availabilities: AssistantAvailability[]): Promise<AssignmentsSetResult> {
    const realAvailabilities = availabilities.filter((a) => a.URID)
    const fakeAvailabilities = availabilities.filter((a) => !a.URID)

    const updateData: UpdateData = {
      assistanceURID,
      availabilitiesToSetURIDs: realAvailabilities.map((a) => a.URID),
      nonAvailabilityAssistantsToSetIDs: fakeAvailabilities.map((a) => {
        return a.assistantId || a.assistant.id
      }),
    }

    return (await this.assignmentsManagerBEService.update(null, updateData)) as unknown as AssignmentsSetResult
  }
}
